import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import {
  Box,
  Flex,
  Icon,
  Image,
  NakedButton,
  Text,
} from '@qga/roo-ui/components';
import { themeGet } from 'styled-system';

import CollapsiblePackageSection, {
  Heading,
} from '@shared/v2/components/CollapsiblePackageSection';
import Select from '@shared/v2/components/Select';

import Button from '@/components/Button';
import useOrigins from '@/hooks/useOrigins';
import { useSectionControls } from '@/v2/components/pages/PackageView/contexts/SectionControlsContext';
import { openLocationPanelDataLayerEvent } from '@/v2/components/pages/PackageView/utils/dataLayer/dataLayer';
import { useGA4Events } from '@/v2/hooks/featureFlags/ga4Events/useGA4Events';
import { handleFlyingFromDatalayerEvent } from '@/v2/utils/dataLayer/searchEvents/changeHandlers';
import { getOriginName } from '@/v2/utils/getOriginName';
import flight from './assets/flight.svg';

interface Props {
  value?: string;
  onSubmit: Function;
  originCodes: string[];
}

const StyledButton = styled(Button)`
  height: 48px;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1.5px;
`;

const StyledSelect = styled(Select)`
  font-weight: 600;
  background-color: ${themeGet('colors.white')};
`;

const getCtaText = (isPanelOpen: boolean) => {
  return isPanelOpen ? 'Cancel' : 'Change location';
};

const FlyingFrom = ({ value = undefined, onSubmit, originCodes }: Props) => {
  const shouldUseNewEvents = useGA4Events();

  const origins = useOrigins(originCodes).map((o) => ({
    ...o,
    value: o.code,
    text: o.name,
  }));

  const {
    flyingFrom: { open, isOpen, close, toggle },
  } = useSectionControls();

  useEffect(() => {
    if (value) {
      close();
    } else {
      open();
    }
  }, [close, open, value]);

  const togglePanel = () => {
    if (!isOpen) {
      openLocationPanelDataLayerEvent();
    }
    toggle();
  };

  const handleChange = (originCode: string) => {
    if (shouldUseNewEvents) {
      handleFlyingFromDatalayerEvent(
        getOriginName(value),
        getOriginName(originCode),
      );
    }
    togglePanel();
    onSubmit(originCode);
  };

  return (
    <CollapsiblePackageSection
      icon={<Image alt="Departing plane icon" src={flight} size="48px" />}
      headerContent={
        isOpen ? (
          <>
            <Heading isBig>Flying from</Heading>
            <Heading>Select your location</Heading>
          </>
        ) : (
          <>
            <Heading>Flying from</Heading>
            <Heading isBig>{getOriginName(value) ?? ''}</Heading>
          </>
        )
      }
      collapseButton={
        isOpen ? (
          <Box textAlign={'center'}>
            <NakedButton
              onClick={togglePanel}
              data-testid="CANCEL_CTA"
              color="brand.primary"
            >
              <Flex gap="6px">
                <Text
                  fontSize="18px"
                  lineHeight="27px"
                  fontWeight="600"
                  style={{ textTransform: 'uppercase' }}
                >
                  {getCtaText(isOpen)}
                </Text>
                <Icon color={'brand.primary'} name="close" mt="2px" />
              </Flex>
            </NakedButton>
          </Box>
        ) : (
          <StyledButton
            data-testid="FLYING_FROM_CTA"
            variant="primary"
            width={['100%', null, '263px']}
            outline
            onClick={togglePanel}
          >
            {getCtaText(isOpen)}
          </StyledButton>
        )
      }
      sectionContent={
        <Box width={['100%', null, '240px']} marginTop={['-8px', null, '0']}>
          <StyledSelect
            data-testid="SELECT"
            id="flying-from"
            textAlign={[null, 'center', 'center']}
            width={['100%', null, 'min-content']}
            minWidth="166px"
            value={value || ''}
            onSelectedChange={handleChange}
            readOnly
            options={origins}
            placeholder="Flying from"
            prefix="From"
          />
        </Box>
      }
      isOpen={isOpen}
      showButton={!isOpen || (isOpen && !!value)}
    />
  );
};

export default FlyingFrom;
